"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  ChevronLeft,
  ChevronRight,
  Clock3,
  Maximize2,
  Mic,
  MicOff,
  Play,
  RotateCcw,
  Square,
  Volume2,
  X,
} from "lucide-react";

import LegalHeader from "@/components/LegalHeader";
import DeviceCheckStep from "@/components/interview-copilot/DeviceCheckStep";
import MicrophoneWaveform from "@/components/interview-copilot/MicrophoneWaveform";
import ResumeSetupStep, {
  CANDIDATE_PROFILE_TEMPLATE,
} from "@/components/interview-copilot/ResumeSetupStep";
import TargetJobStep, {
  MIN_JD_LENGTH,
} from "@/components/interview-copilot/TargetJobStep";
import {
  useVisualDeliveryCoach,
  VisualDeliveryResults,
  VisualDeliverySessionPanel,
  VisualDeliverySetup,
} from "@/components/interview-copilot/VisualDeliveryCoach";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";
import {
  getInterviewRuntimeContent,
  interpolateInterviewRuntime,
  type InterviewRuntimeCopy,
} from "@/i18n/interviewRuntimeContent";
import { startAudioQualityMonitor } from "@/lib/interview/audioQuality";
import { speechPersonality, visemeForText, type AvatarState, type MouthShape } from "@/lib/interview/v2/avatar";

import InterviewerAvatar from "./InterviewerAvatar";

type Question = {
  question_id: string;
  text: string;
  competency: string;
  kind: string;
  is_follow_up: boolean;
};

type Progress = {
  title: string;
  config: {
    coaching_mode: boolean;
    language: string;
    voice: string;
    avatar: "jobolar-nova" | "jobolar-orion" | "none";
    personality: "warm" | "direct" | "analytical";
  };
  status: string;
  currentIndex: number;
  totalQuestions: number;
  completedAnswers: number;
  currentQuestion: Question | null;
  latestEvaluation?: Record<string, any>;
  evaluations?: Array<Record<string, any>>;
  report?: Record<string, any>;
};

type MockUsage = {
  mode: "mock";
  plan: string;
  limitSeconds: number;
  usedSeconds: number;
  reservedSeconds: number;
  remainingSeconds: number;
  resetAt: string;
};

type MockHistoryEntry = {
  sessionId: string;
  status: string;
  language: string;
  settings: Record<string, unknown>;
  billableSeconds: number;
  startedAt: string;
  endedAt: string | null;
};

type MockOverview = {
  loading: boolean;
  unavailable: boolean;
  usage: MockUsage | null;
  history: MockHistoryEntry[];
};

const EMPTY_OVERVIEW: MockOverview = {
  loading: true,
  unavailable: false,
  usage: null,
  history: [],
};

const SETUP_STEP_IDS = ["resume", "job", "practice", "device"] as const;

function runtimeText(
  template: string,
  values: Record<string, string | number>,
): string {
  return interpolateInterviewRuntime(template, values);
}

function mockApiErrorMessage(
  payload: Record<string, unknown>,
  status: number,
  copy: InterviewRuntimeCopy,
  fallback: string,
): string {
  const code = String(payload?.code || "");
  if (["INTERVIEW_SUBSCRIPTION_REQUIRED", "INTERVIEW_PLAN_REQUIRED"].includes(code)) {
    return copy.errorSubscription;
  }
  if (code === "INTERVIEW_MONTHLY_LIMIT_REACHED") return copy.errorMonthlyLimit;
  if (code === "INTERVIEW_AUDIO_NO_CLEAR_SPEECH") return copy.errorAudioNoSpeech;
  if (["INTERVIEW_AUDIO_SIZE_INVALID", "INTERVIEW_AUDIO_TYPE_INVALID"].includes(code)) {
    return copy.errorAudioInvalid;
  }
  if (["INTERVIEW_V2_INVALID_REQUEST", "INTERVIEW_LANGUAGE_INVALID", "INTERVIEW_OPERATION_ID_INVALID"].includes(code)) {
    return copy.errorRequestInvalid;
  }
  if (["INTERVIEW_SESSION_EXPIRED", "INTERVIEW_SESSION_NOT_FOUND"].includes(code)) {
    return copy.errorSessionExpired;
  }
  if (code === "INTERVIEW_SESSION_NOT_ACTIVE") return copy.errorSessionInactive;
  if (code === "INTERVIEW_ACTIVE_SESSION_EXISTS") return copy.errorActiveElsewhere;
  if (code === "INTERVIEW_SMART_LISTEN_PRO_REQUIRED") return copy.errorSmartPro;
  if (["INTERVIEW_CAPACITY_UNAVAILABLE", "INTERVIEW_ROUTING_FAILED", "INTERVIEW_FAILOVER_UNAVAILABLE"].includes(code)) {
    return copy.errorCapacity;
  }
  if (status === 401) return copy.errorAuth;
  if (status === 402 || status === 403) return copy.errorSubscription;
  if (status === 404 || status === 410) return copy.errorSessionExpired;
  if (status === 429) return copy.errorRateLimit;
  if (status >= 500) return copy.errorUnavailable;
  return fallback;
}

const EMPTY_RESUME = {
  source: null,
  slot: null,
  name: "",
  fileUrl: "",
  text: "",
  candidateName: "",
  candidateEmail: "",
  candidatePhone: "",
  candidateProfile: CANDIDATE_PROFILE_TEMPLATE,
  confirmed: false,
};

const EMPTY_JOB = {
  title: "",
  company: "",
  description: "",
  interviewType: "general",
  experienceLevel: "",
  focusAreas: "",
};

const MIC_STATE = {
  UNCHECKED: "unchecked",
  CHECKING: "checking",
  GRANTED: "granted",
  PASSED: "passed",
  DENIED: "denied",
} as const;

const GET_READY_COUNTDOWN = [5, 4, 3, 2, 1] as const;
const MOCK_HISTORY_KEY = "jobolar-interview-mock-history-v2";
const MOCK_RECOVERY_KEY = "jobolar-interview-mock-recovery-v2";

function readLocalMockHistory(): MockHistoryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MOCK_HISTORY_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.slice(0, 100) : [];
  } catch {
    return [];
  }
}

function writeLocalMockHistory(entries: MockHistoryEntry[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MOCK_HISTORY_KEY, JSON.stringify(entries.slice(0, 100)));
}

function wait(milliseconds: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
}

export default function MockInterviewClient() {
  const router = useRouter();
  const { locale } = useLanguage();
  const runtime = useMemo(() => getInterviewRuntimeContent(locale), [locale]);
  const t = useMemo(
    () => ({
      ...getStrings(locale, "interview_copilot"),
      ...getStrings(locale, "copilot_product"),
      ...getInterviewRuntimeContent(locale),
    }),
    [locale],
  );
  const setupSteps = useMemo(
    () => [
      { id: "resume" as const, label: runtime.stepResume },
      { id: "job" as const, label: runtime.stepJob },
      { id: "practice" as const, label: runtime.stepPractice },
      { id: "device" as const, label: runtime.stepDevice },
    ],
    [runtime],
  );
  const interviewLanguage = String(locale || "en").toLowerCase().split("-")[0];
  const [sessionId, setSessionId] = useState("");
  const recoveryContextToken = useRef("");
  const progressToken = useRef("");
  const [progress, setProgress] = useState<Progress | null>(null);
  const [report, setReport] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState("");
  const [connectionNotice, setConnectionNotice] = useState("");
  const [handoffMessage, setHandoffMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [ending, setEnding] = useState(false);
  const [getReadySeconds, setGetReadySeconds] = useState<number | null>(null);
  const [startingSession, setStartingSession] = useState(false);
  const [endedEarly, setEndedEarly] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [interviewerMuted, setInterviewerMuted] = useState(false);
  const [activeStep, setActiveStep] = useState<(typeof SETUP_STEP_IDS)[number]>("resume");
  const [resumeSetup, setResumeSetup] = useState(EMPTY_RESUME);
  const [jobSetup, setJobSetup] = useState(EMPTY_JOB);
  const [micState, setMicState] = useState<string>(MIC_STATE.UNCHECKED);
  const [microphoneDeviceId, setMicrophoneDeviceId] = useState("");
  const [textOnly, setTextOnly] = useState(false);
  const [difficulty, setDifficulty] = useState("standard");
  const [durationMinutes, setDurationMinutes] = useState(20);
  const [coachingMode, setCoachingMode] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voice, setVoice] = useState("auto");
  const [avatar, setAvatar] = useState<"jobolar-nova" | "jobolar-orion" | "none">("jobolar-nova");
  const [personality, setPersonality] = useState<"warm" | "direct" | "analytical">("warm");
  const [avatarState, setAvatarState] = useState<AvatarState>("idle");
  const [mouth, setMouth] = useState<MouthShape>("closed");
  const [customAvatarUrl, setCustomAvatarUrl] = useState("");
  const [customAvatarConsent, setCustomAvatarConsent] = useState(false);
  const [overview, setOverview] = useState<MockOverview>(EMPTY_OVERVIEW);
  const [clearHistoryPrompt, setClearHistoryPrompt] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [historyNotice, setHistoryNotice] = useState("");
  const [focusedPanel, setFocusedPanel] = useState<MockResultPanel | null>(null);
  const customAvatarRef = useRef("");
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const stream = useRef<MediaStream | null>(null);
  const qualityMonitor = useRef<ReturnType<typeof startAudioQualityMonitor>>(null);
  const submitOnStop = useRef(true);
  const startRecordingRef = useRef<() => Promise<void>>(async () => undefined);
  const spokenQuestion = useRef("");
  const sessionEndedRef = useRef(false);
  const visualDelivery = useVisualDeliveryCoach({
    suspendAnalysis:
      busy ||
      ending ||
      avatarState === "speaking" ||
      Boolean(sessionId && !progress?.currentQuestion),
    suspensionReason:
      avatarState === "speaking"
        ? runtime.visualPausedSpeaking
        : runtime.visualPausedProcessing,
  });

  const activeStepIndex = setupSteps.findIndex((step) => step.id === activeStep);
  const audioSupported =
    typeof window !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function" &&
    typeof window.MediaRecorder === "function";
  const resumeReady = Boolean(resumeSetup.text.trim() && resumeSetup.confirmed);
  const jobReady = Boolean(
    jobSetup.title.trim() && jobSetup.description.trim().length >= MIN_JD_LENGTH,
  );
  const deviceReady = micState === MIC_STATE.PASSED;
  const setupReady = resumeReady && jobReady && deviceReady;

  const refreshMockOverview = useCallback(async () => {
    try {
      const response = await fetch("/api/interview/v2/mock/sessions", {
        method: "GET",
        cache: "no-store",
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setOverview({
          loading: false,
          unavailable: true,
          usage: null,
          history: readLocalMockHistory(),
        });
        return;
      }
      setOverview({
        loading: false,
        unavailable: false,
        usage: payload.usage ?? null,
        history: [
          ...readLocalMockHistory(),
          ...(Array.isArray(payload.history) ? payload.history : []),
        ].filter(
          (entry, index, entries) =>
            entries.findIndex((candidate) => candidate.sessionId === entry.sessionId) === index,
        ),
      });
    } catch {
      setOverview({
        loading: false,
        unavailable: true,
        usage: null,
        history: readLocalMockHistory(),
      });
    }
  }, []);

  useEffect(() => {
    void refreshMockOverview();
  }, [refreshMockOverview]);

  const clearMockHistory = useCallback(async () => {
    setClearingHistory(true);
    setHistoryNotice("");
    try {
      const localCount = readLocalMockHistory().length;
      writeLocalMockHistory([]);
      const response = await fetch("/api/interview/v2/mock/sessions", {
        method: "DELETE",
        cache: "no-store",
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          mockApiErrorMessage(payload, response.status, runtime, runtime.historyClearError),
        );
      }
      const clearedCount = localCount + Math.max(0, Number(payload.clearedCount) || 0);
      await refreshMockOverview();
      setClearHistoryPrompt(false);
      setHistoryNotice(
        clearedCount === 1
          ? runtime.historyClearedOne
          : clearedCount > 1
            ? runtimeText(runtime.historyClearedMany, { count: clearedCount })
            : runtime.historyNoneToClear,
      );
    } catch (clearError) {
      setHistoryNotice(
        clearError instanceof Error
          ? clearError.message
          : runtime.historyClearError,
      );
    } finally {
      setClearingHistory(false);
    }
  }, [refreshMockOverview, runtime]);

  const rememberMockSession = useCallback(
    (status: string) => {
      if (!sessionId) return;
      const now = new Date().toISOString();
      const entry: MockHistoryEntry = {
        sessionId,
        status,
        language: interviewLanguage,
        settings: {
          interviewType: jobSetup.interviewType,
          role: jobSetup.title,
          company: jobSetup.company,
        },
        billableSeconds: elapsedSeconds,
        startedAt: new Date(Date.now() - elapsedSeconds * 1000).toISOString(),
        endedAt: now,
      };
      const next = [
        entry,
        ...readLocalMockHistory().filter((item) => item.sessionId !== sessionId),
      ].slice(0, 100);
      writeLocalMockHistory(next);
      setOverview((current) => ({ ...current, history: next }));
    },
    [elapsedSeconds, interviewLanguage, jobSetup.company, jobSetup.interviewType, jobSetup.title, sessionId],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const extensionId = params.get("extensionId") || "";
    const handoff = params.get("handoff") || "";
    if (
      params.get("source") !== "extension" ||
      !/^[a-p]{32}$/.test(extensionId) ||
      !/^[0-9a-f-]{36}$/i.test(handoff)
    ) return;
    const extensionRuntime = (globalThis as any).chrome?.runtime;
    if (typeof extensionRuntime?.sendMessage !== "function") return;
    let active = true;
    extensionRuntime.sendMessage(
      extensionId,
      { type: "JOBOLAR_INTERVIEW_MOCK_SETUP", handoff },
      (reply: any) => {
        if (!active) return;
        if (extensionRuntime.lastError || !reply?.ok || !reply.setup) {
          setHandoffMessage(runtime.handoffReopen);
          return;
        }
        const setup = reply.setup as Record<string, unknown>;
        const resumeText = String(setup.resumeText || "");
        setResumeSetup((current) => ({
          ...current,
          text: resumeText,
          name: String(setup.resumeFileName || "Imported from Interview Assistant"),
          candidateName: String(setup.candidateName || ""),
          candidateProfile: String(setup.candidateProfile || CANDIDATE_PROFILE_TEMPLATE),
          confirmed: Boolean(resumeText.trim()),
        }));
        const interviewType = String(setup.interviewType || "general")
          .toLowerCase()
          .replace("role-specific", "role_specific");
        setJobSetup((current) => ({
          ...current,
          title: String(setup.targetRole || ""),
          company: String(setup.company || ""),
          description: String(setup.jobDescription || ""),
          interviewType,
          experienceLevel: String(setup.experienceLevel || ""),
          focusAreas: String(setup.focusAreas || ""),
        }));
        setHandoffMessage(runtime.handoffImported);
        window.history.replaceState({}, "", "/interview/mock");
      },
    );
    return () => { active = false; };
  }, [runtime.handoffImported, runtime.handoffReopen]);

  useEffect(() => {
    const load = () => setVoices(window.speechSynthesis?.getVoices() ?? []);
    load();
    window.speechSynthesis?.addEventListener("voiceschanged", load);
    return () => window.speechSynthesis?.removeEventListener("voiceschanged", load);
  }, []);

  useEffect(() => () => {
    if (customAvatarRef.current) URL.revokeObjectURL(customAvatarRef.current);
  }, []);

  useEffect(() => {
    const question = progress?.currentQuestion;
    if (
      !question ||
      interviewerMuted ||
      spokenQuestion.current === question.question_id
    ) return;
    spokenQuestion.current = question.question_id;
    const utterance = new SpeechSynthesisUtterance(question.text);
    utterance.lang = progress?.config.language ?? "en";
    const selected = voices.find((item) => item.voiceURI === progress?.config.voice);
    if (selected) utterance.voice = selected;
    const delivery = speechPersonality(progress.config.personality);
    utterance.rate = delivery.rate;
    utterance.pitch = delivery.pitch;
    utterance.onstart = () => { setAvatarState("speaking"); setMouth("open"); };
    utterance.onboundary = (event) => {
      const boundaryText = question.text.slice(event.charIndex, event.charIndex + (event.charLength || 4));
      setMouth(visemeForText(boundaryText));
    };
    utterance.onend = () => { setAvatarState("idle"); setMouth("closed"); };
    utterance.onerror = () => { setAvatarState("idle"); setMouth("closed"); };
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  }, [progress, interviewerMuted, voices]);

  useEffect(() => {
    if (!sessionId || report || endedEarly) return;
    const timer = window.setInterval(
      () => setElapsedSeconds((current) => current + 1),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, [sessionId, report, endedEarly]);

  useEffect(() => {
    if (sessionId) return;
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(MOCK_RECOVERY_KEY) || "null");
      if (!saved?.sessionId || !saved?.progress || !saved?.progressToken) return;
      recoveryContextToken.current = String(saved.recoveryContextToken || "");
      progressToken.current = String(saved.progressToken || "");
      setSessionId(String(saved.sessionId));
      setProgress(saved.progress as Progress);
      setElapsedSeconds(Math.max(0, Number(saved.elapsedSeconds) || 0));
      sessionEndedRef.current = false;
    } catch {
      window.sessionStorage.removeItem(MOCK_RECOVERY_KEY);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !progress?.currentQuestion || endedEarly) return;
    const handleSpacebar = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        event.code !== "Space" ||
        event.repeat ||
        target?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(target?.tagName ?? "")
      ) return;
      event.preventDefault();
      if (busy || recording) return;
      if (interviewerMuted) {
        setInterviewerMuted(false);
        spokenQuestion.current = "";
        setProgress((current) => (current ? { ...current } : current));
      } else {
        window.speechSynthesis.cancel();
        setInterviewerMuted(true);
        setAvatarState("idle");
        setMouth("closed");
      }
    };
    window.addEventListener("keydown", handleSpacebar);
    return () => window.removeEventListener("keydown", handleSpacebar);
  }, [busy, endedEarly, interviewerMuted, progress?.currentQuestion, recording, sessionId]);

  useEffect(() => {
    if (!sessionId || report || endedEarly) return;
    const heartbeat = async () => {
      try {
        const response = await fetch(`/api/interview/v2/sessions/${sessionId}/heartbeat`, {
          method: "POST",
          cache: "no-store",
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(runtime.reconnecting);
        if (payload.session?.status === "paused") {
          const resumed = await fetch(`/api/interview/v2/sessions/${sessionId}/resume`, {
            method: "POST",
            cache: "no-store",
          });
          if (!resumed.ok) throw new Error(runtime.reconnecting);
        }
        setConnectionNotice("");
        await refreshMockOverview();
      } catch {
        setConnectionNotice(runtime.reconnecting);
      }
    };
    void heartbeat();
    const interval = window.setInterval(() => void heartbeat(), 30_000);
    return () => window.clearInterval(interval);
  }, [sessionId, report, endedEarly, refreshMockOverview, runtime.reconnecting]);

  async function start() {
    if (!setupReady) {
      setError(runtime.setupIncomplete);
      return;
    }
    setBusy(true);
    setStartingSession(true);
    setError("");
    try {
      setGetReadySeconds(GET_READY_COUNTDOWN[0]);
      const sessionRequest = (async () => {
        const response = await fetch("/api/interview/v2/mock/sessions", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            role: jobSetup.title.trim(),
            company: jobSetup.company.trim(),
            interviewType: jobSetup.interviewType,
            language: ["en", "fr", "de", "es"].includes(interviewLanguage)
              ? interviewLanguage
              : "en",
            difficulty,
            durationMinutes,
            coachingMode,
            voice,
            avatar,
            personality,
            resumeText: resumeSetup.text,
            candidateProfile: resumeSetup.candidateProfile,
            candidateName: resumeSetup.candidateName,
            jobDescription: jobSetup.description,
            experienceLevel: jobSetup.experienceLevel,
            focusAreas: jobSetup.focusAreas,
          }),
        });
        return { response, payload: await response.json() };
      })();
      // The request intentionally starts before the countdown. Attaching a
      // rejection handler immediately avoids an unhandled rejection if the
      // network fails while the countdown is still visible.
      void sessionRequest.catch(() => undefined);
      for (const seconds of GET_READY_COUNTDOWN) {
        setGetReadySeconds(seconds);
        await wait(1_000);
      }
      setGetReadySeconds(null);
      const { response, payload } = await sessionRequest;
      if (!response.ok) {
        throw new Error(
          mockApiErrorMessage(payload, response.status, runtime, runtime.startFailed),
        );
      }
      sessionEndedRef.current = false;
      setSessionId(payload.session.sessionId);
      setProgress(payload.progress);
      recoveryContextToken.current = String(payload.recoveryContextToken || "");
      progressToken.current = String(payload.progressToken || "");
      window.sessionStorage.setItem(
        MOCK_RECOVERY_KEY,
        JSON.stringify({
          sessionId: payload.session.sessionId,
          progress: payload.progress,
          recoveryContextToken: recoveryContextToken.current,
          progressToken: progressToken.current,
          elapsedSeconds: 0,
        }),
      );
      setElapsedSeconds(0);
      setEndedEarly(false);
      visualDelivery.beginSession();
      void refreshMockOverview();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : runtime.startFailed);
    } finally {
      setGetReadySeconds(null);
      setStartingSession(false);
      setBusy(false);
    }
  }

  async function beginRecording() {
    setError("");
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...(microphoneDeviceId
            ? { deviceId: { exact: microphoneDeviceId } }
            : {}),
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "";
      const active = new MediaRecorder(media, mimeType ? { mimeType } : undefined);
      stream.current = media;
      qualityMonitor.current = startAudioQualityMonitor(media);
      chunks.current = [];
      submitOnStop.current = true;
      active.ondataavailable = (event) => event.data.size && chunks.current.push(event.data);
      active.onstop = () => {
        const quality = qualityMonitor.current?.stop();
        qualityMonitor.current = null;
        if (submitOnStop.current) {
          if (quality?.analysisAvailable && !quality.passed) {
            setError(t.audio_no_clear_speech);
          } else {
            void uploadAnswer(new Blob(chunks.current, { type: active.mimeType || "audio/webm" }));
          }
        }
        chunks.current = [];
      };
      recorder.current = active;
      window.speechSynthesis.cancel();
      active.start(500);
      setRecording(true);
      setAvatarState("listening");
      setMouth("closed");
    } catch (caught) {
      qualityMonitor.current?.stop();
      qualityMonitor.current = null;
      const name = caught instanceof DOMException ? caught.name : "";
      setError(name === "NotAllowedError" ? t.mic_error_permission : runtime.microphoneFailed);
    }
  }

  function stopRecording() {
    submitOnStop.current = true;
    recorder.current?.stop();
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    setRecording(false);
    setAvatarState("idle");
  }

  function discardRecording() {
    submitOnStop.current = false;
    qualityMonitor.current?.stop();
    qualityMonitor.current = null;
    if (recorder.current?.state === "recording") recorder.current.stop();
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    setRecording(false);
    setAvatarState("idle");
  }

  async function uploadAnswer(audio: Blob) {
    if (!sessionId) return;
    setBusy(true);
    setError("");
    try {
      const operationId = crypto.randomUUID();
      const submit = async (includeRecovery: boolean) => {
        const body = new FormData();
        body.set("audio", audio, "candidate-answer.webm");
        body.set("progressToken", progressToken.current);
        if (includeRecovery && recoveryContextToken.current) {
          body.set("recoveryContextToken", recoveryContextToken.current);
        }
        const response = await fetch(`/api/interview/v2/mock/sessions/${sessionId}/answers/audio`, {
          method: "POST",
          headers: { "Idempotency-Key": operationId },
          body,
        });
        return { response, payload: await response.json().catch(() => ({})) };
      };
      let { response, payload } = await submit(false);
      if (
        !response.ok &&
        payload?.code === "INTERVIEW_RECOVERY_REQUIRED" &&
        recoveryContextToken.current
      ) {
        ({ response, payload } = await submit(true));
      }
      if (!response.ok) {
        throw new Error(
          mockApiErrorMessage(payload, response.status, runtime, runtime.evaluateFailed),
        );
      }
      setProgress(payload.progress);
      progressToken.current = String(payload.progressToken || progressToken.current);
      window.sessionStorage.setItem(
        MOCK_RECOVERY_KEY,
        JSON.stringify({
          sessionId,
          progress: payload.progress,
          recoveryContextToken: recoveryContextToken.current,
          progressToken: progressToken.current,
          elapsedSeconds,
        }),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : runtime.evaluateFailed);
    } finally {
      setBusy(false);
    }
  }

  startRecordingRef.current = beginRecording;

  function toggleInterviewerAudio() {
    if (interviewerMuted) {
      setInterviewerMuted(false);
      spokenQuestion.current = "";
      setProgress((current) => (current ? { ...current } : current));
      return;
    }
    window.speechSynthesis.cancel();
    setInterviewerMuted(true);
    setAvatarState("idle");
    setMouth("closed");
  }

  useEffect(() => {
    if (!sessionId || sessionEndedRef.current) return undefined;
    const url = `/api/interview/v2/sessions/${sessionId}/end`;
    const releaseOnExit = (event: PageTransitionEvent) => {
      // A page kept in the back/forward cache can resume without starting a
      // new interview. Only release the server session on a real page exit.
      if (event.persisted || sessionEndedRef.current) return;
      navigator.sendBeacon(url);
      sessionEndedRef.current = true;
    };
    window.addEventListener("pagehide", releaseOnExit);
    return () => window.removeEventListener("pagehide", releaseOnExit);
  }, [sessionId]);

  async function endSession(): Promise<boolean> {
    if (!sessionId || sessionEndedRef.current) return true;
    if (ending) return false;
    discardRecording();
    window.speechSynthesis.cancel();
    setEnding(true);
    setError("");
    try {
      const response = await fetch(`/api/interview/v2/sessions/${sessionId}/end`, {
        method: "POST",
        cache: "no-store",
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(
          mockApiErrorMessage(payload, response.status, runtime, runtime.endFailed),
        );
      }
      sessionEndedRef.current = true;
      setEndedEarly(true);
      rememberMockSession("ended");
      recoveryContextToken.current = "";
      progressToken.current = "";
      window.sessionStorage.removeItem(MOCK_RECOVERY_KEY);
      visualDelivery.finishSession();
      await refreshMockOverview();
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : runtime.endFailed);
      return false;
    } finally {
      setEnding(false);
    }
  }

  async function chooseMode() {
    if (!(await endSession())) return;
    router.push("/interview");
  }

  async function buildReport() {
    if (!sessionId) return;
    setBusy(true);
    setError("");
    try {
      const operationId = crypto.randomUUID();
      const submit = async (includeRecovery: boolean) => {
        const response = await fetch(`/api/interview/v2/mock/sessions/${sessionId}/report`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": operationId,
          },
          body: JSON.stringify({
            progressToken: progressToken.current,
            ...(includeRecovery && recoveryContextToken.current
              ? { recoveryContextToken: recoveryContextToken.current }
              : {}),
          }),
        });
        return { response, payload: await response.json().catch(() => ({})) };
      };
      let { response, payload } = await submit(false);
      if (
        !response.ok &&
        payload?.code === "INTERVIEW_RECOVERY_REQUIRED" &&
        recoveryContextToken.current
      ) {
        ({ response, payload } = await submit(true));
      }
      if (!response.ok) {
        throw new Error(
          mockApiErrorMessage(payload, response.status, runtime, runtime.reportFailed),
        );
      }
      sessionEndedRef.current = true;
      setProgress(payload.progress);
      setReport(payload.report);
      rememberMockSession("completed");
      recoveryContextToken.current = "";
      progressToken.current = "";
      window.sessionStorage.removeItem(MOCK_RECOVERY_KEY);
      visualDelivery.finishSession();
      await refreshMockOverview();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : runtime.reportFailed);
    } finally {
      setBusy(false);
    }
  }

  function chooseCustomAvatar(file: File | undefined) {
    if (!file) return;
    if (!customAvatarConsent) {
      setError(runtime.avatarConsentRequired);
      return;
    }
    if (!new Set(["image/png", "image/jpeg", "image/webp"]).has(file.type) || file.size > 5 * 1024 * 1024) {
      setError(runtime.avatarFileInvalid);
      return;
    }
    if (customAvatarRef.current) URL.revokeObjectURL(customAvatarRef.current);
    const url = URL.createObjectURL(file);
    customAvatarRef.current = url;
    setCustomAvatarUrl(url);
    setError("");
  }

  function updateCustomAvatarConsent(approved: boolean) {
    setCustomAvatarConsent(approved);
    if (!approved && customAvatarRef.current) {
      URL.revokeObjectURL(customAvatarRef.current);
      customAvatarRef.current = "";
      setCustomAvatarUrl("");
    }
  }

  if (!sessionId || !progress) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-blue-50">
        <LegalHeader />
        {getReadySeconds !== null || startingSession ? (
          <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/80 px-5 text-white" role="status" aria-live="assertive" aria-label={getReadySeconds !== null ? runtimeText(runtime.getReadyAria, { seconds: getReadySeconds }) : runtime.preparingInterviewer}>
            <div className="text-center">
              <p className="text-xl font-extrabold uppercase tracking-[0.22em] text-indigo-200">{runtime.getReady}</p>
              {getReadySeconds !== null ? (
                <p className="mt-4 text-8xl font-black tabular-nums">{getReadySeconds}</p>
              ) : (
                <div className="mx-auto mt-6 h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-white" aria-hidden="true" />
              )}
              <p className="mt-5 text-sm font-medium text-slate-200">
                {runtime.preparingBackground}
              </p>
            </div>
          </div>
        ) : null}
        <main className="mx-auto max-w-[1480px] px-4 py-10 sm:px-6 lg:px-8">
          <div className="mb-7 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-600">
              {runtime.mockEyebrow}
            </p>
            <h1 className="mt-2 text-3xl font-bold text-slate-950 sm:text-4xl">
              {runtime.mockTitle}
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-slate-600">
              {runtime.mockSubtitle}
            </p>
            <Link href="/interview" className="mt-5 inline-flex items-center gap-2 text-lg font-extrabold text-indigo-700 hover:text-indigo-900">
              <ChevronLeft className="h-5 w-5" /> {runtime.chooseMode}
            </Link>
            {handoffMessage ? <p className="mx-auto mt-4 max-w-2xl rounded-xl border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-800" role="status">{handoffMessage}</p> : null}
          </div>

          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-indigo-100/60">
              <ol className="grid grid-cols-2 border-b border-slate-200 bg-slate-50 sm:grid-cols-4">
              {setupSteps.map((step, index) => {
                const selected = step.id === activeStep;
                const complete =
                  (step.id === "resume" && resumeReady) ||
                  (step.id === "job" && jobReady) ||
                  (step.id === "practice") ||
                  (step.id === "device" && deviceReady);
                return (
                  <li key={step.id}>
                    <button
                      type="button"
                      onClick={() => setActiveStep(step.id)}
                      className={`flex w-full items-center justify-center gap-2 px-3 py-4 text-sm font-semibold transition ${
                        selected
                          ? "bg-indigo-600 text-white"
                          : "text-slate-600 hover:bg-indigo-50"
                      }`}
                    >
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${selected ? "bg-white/20" : complete ? "bg-emerald-100 text-emerald-700" : "bg-slate-200"}`}>
                        {complete ? "✓" : index + 1}
                      </span>
                      {step.label}
                    </button>
                  </li>
                );
              })}
              </ol>

              <div className="p-5 sm:p-8">
              {activeStep === "resume" ? (
                <ResumeSetupStep
                  t={t}
                  value={resumeSetup}
                  onChange={setResumeSetup}
                  notice={runtime.resumePrivacyNotice}
                />
              ) : null}

              {activeStep === "job" ? (
                <TargetJobStep t={t} value={jobSetup} onChange={setJobSetup} />
              ) : null}

              {activeStep === "practice" ? (
                <div className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <SetupSelect label={runtime.difficulty} value={difficulty} onChange={setDifficulty}>
                      <option value="introductory">{runtime.introductory}</option>
                      <option value="standard">{runtime.standard}</option>
                      <option value="advanced">{runtime.advanced}</option>
                    </SetupSelect>
                    <SetupSelect label={runtime.practiceDuration} value={String(durationMinutes)} onChange={(value) => setDurationMinutes(Number(value))}>
                      {[12, 20, 30, 45, 60].map((minutes) => <option key={minutes} value={minutes}>{runtimeText(runtime.minutes, { count: minutes })}</option>)}
                    </SetupSelect>
                    <SetupSelect label={runtime.interviewerVoice} value={voice} onChange={setVoice}>
                      <option value="auto">{runtime.automaticVoice}</option>
                      {voices.map((item) => <option key={item.voiceURI} value={item.voiceURI}>{item.name} · {item.lang}</option>)}
                    </SetupSelect>
                    <SetupSelect label={runtime.interviewer} value={avatar} onChange={(value) => setAvatar(value as typeof avatar)}>
                      <option value="jobolar-nova">{runtime.nova}</option>
                      <option value="jobolar-orion">{runtime.orion}</option>
                      <option value="none">{runtime.voiceOnly}</option>
                    </SetupSelect>
                    <SetupSelect label={runtime.personality} value={personality} onChange={(value) => setPersonality(value as typeof personality)}>
                      <option value="warm">{runtime.warm}</option>
                      <option value="direct">{runtime.direct}</option>
                      <option value="analytical">{runtime.analytical}</option>
                    </SetupSelect>
                  </div>
                  <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    <input type="checkbox" checked={coachingMode} onChange={(event) => setCoachingMode(event.target.checked)} className="mt-0.5" />
                    <span><strong className="block text-slate-900">{runtime.coachingMode}</strong>{runtime.coachingModeHelp}</span>
                  </label>
                  <VisualDeliverySetup controller={visualDelivery} />
                  {process.env.NEXT_PUBLIC_INTERVIEW_CUSTOM_AVATAR_ENABLED === "true" ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                      <p className="text-sm font-semibold">{runtime.avatarExperimental}</p>
                      <label className="mt-2 flex items-start gap-2 text-xs text-slate-700"><input type="checkbox" checked={customAvatarConsent} onChange={(event) => updateCustomAvatarConsent(event.target.checked)} />{runtime.avatarConsent}</label>
                      <input type="file" accept="image/png,image/jpeg,image/webp" disabled={!customAvatarConsent} onChange={(event) => chooseCustomAvatar(event.target.files?.[0])} className="mt-3 text-sm" />
                      <p className="mt-2 text-xs text-slate-600">{runtime.avatarLocal}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {activeStep === "device" ? (
                <DeviceCheckStep
                  t={t}
                  micState={micState}
                  onMicStateChange={setMicState}
                  textOnly={textOnly}
                  onTextOnlyChange={setTextOnly}
                  audioSupported={audioSupported}
                  allowTextOnly={false}
                  initialDeviceId={microphoneDeviceId}
                  onDeviceIdChange={setMicrophoneDeviceId}
                />
              ) : null}

              <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-5">
                <button type="button" disabled={activeStepIndex === 0} onClick={() => setActiveStep(setupSteps[activeStepIndex - 1].id)} className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 disabled:invisible">
                  <ChevronLeft className="h-4 w-4" /> {runtime.back}
                </button>
                {activeStepIndex < setupSteps.length - 1 ? (
                  <button type="button" onClick={() => setActiveStep(setupSteps[activeStepIndex + 1].id)} className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
                    {runtime.continue} <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button type="button" onClick={() => void start()} disabled={busy || !setupReady} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
                    <Play className="h-4 w-4" />{busy ? runtime.buildingInterview : runtime.beginMock}
                  </button>
                )}
              </div>

              <div className="mt-5 grid gap-2 text-xs text-slate-600 sm:grid-cols-3">
                <Readiness ready={resumeReady} label={runtime.resumeConfirmed} />
                <Readiness ready={jobReady} label={runtime.jobAdded} />
                <Readiness ready={deviceReady} label={runtime.microphoneTested} />
              </div>
              {error ? <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
              <p className="mt-4 text-xs text-slate-500">{runtime.coachingDisclaimer}</p>
              </div>
            </section>
            <aside className="space-y-4">
              <MockMonthlyUsageCard
                overview={overview}
                activeSeconds={0}
                sessionActive={false}
                copy={runtime}
              />
              <MockSessionHistoryCard
                overview={overview}
                confirmClear={clearHistoryPrompt}
                clearing={clearingHistory}
                notice={historyNotice}
                onRequestClear={() => {
                  setHistoryNotice("");
                  setClearHistoryPrompt(true);
                }}
                onCancelClear={() => setClearHistoryPrompt(false)}
                onConfirmClear={() => void clearMockHistory()}
                copy={runtime}
              />
            </aside>
          </div>
        </main>
      </div>
    );
  }

  const question = progress.currentQuestion;
  const latestEvaluation = progress.latestEvaluation;
  const resultPanels: MockResultPanel[] = [
    { title: runtime.keyPoints, value: latestEvaluation?.strengths },
    { title: runtime.fullAnswer, value: latestEvaluation?.improved_sample_answer },
    {
      title: runtime.insights,
      value: [
        ...(Array.isArray(latestEvaluation?.improvements) ? latestEvaluation.improvements : []),
        ...(Array.isArray(latestEvaluation?.confidence_indicators)
          ? latestEvaluation.confidence_indicators
          : []),
      ],
    },
    {
      title: runtime.followups,
      value: Array.isArray(latestEvaluation?.adaptive_follow_up)
        ? latestEvaluation.adaptive_follow_up
        : latestEvaluation?.adaptive_follow_up
          ? [latestEvaluation.adaptive_follow_up]
          : [],
    },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-blue-50">
      <MockFocusedPanel
        panel={focusedPanel}
        language={progress.config.language}
        copy={runtime}
        onClose={() => setFocusedPanel(null)}
      />
      <LegalHeader />
      <main className="mx-auto max-w-[1480px] space-y-5 px-4 py-8 sm:px-6 lg:px-8">
        <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${endedEarly ? "bg-amber-500" : "animate-pulse bg-emerald-500"}`} />
                <p className="text-sm font-semibold text-indigo-700">{runtime.mockTitle} · {endedEarly ? runtime.ended : runtime.livePractice}</p>
              </div>
              <h1 className="mt-1 text-xl font-bold text-slate-950">{progress.title}</h1>
              <p className="mt-1 text-sm text-slate-600">{jobSetup.title}{jobSetup.company ? ` · ${jobSetup.company}` : ""} · {runtimeText(runtime.answersCaptured, { count: progress.completedAnswers })}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => void chooseMode()} disabled={ending} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                {ending ? runtime.endingSession : runtime.chooseMode}
              </button>
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 font-mono text-sm font-semibold text-slate-700">
                <Clock3 className="h-4 w-4" />{formatDuration(elapsedSeconds)}
              </span>
              {!endedEarly && !report ? (
                <button type="button" onClick={() => void endSession()} disabled={ending} className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50">
                  <Square className="h-4 w-4" />{ending ? runtime.ending : runtime.endSession}
                </button>
              ) : null}
            </div>
          </div>
        </header>

        {endedEarly ? (
          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <section className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
              <h2 className="text-2xl font-bold text-slate-950">{runtime.endedTitle}</h2>
              <p className="mt-2 text-slate-600">{runtime.endedBody}</p>
              <button type="button" onClick={() => window.location.reload()} className="mt-5 rounded-full bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white">{runtime.startNewPractice}</button>
            </section>
            <aside className="space-y-4">
              <MockMonthlyUsageCard
                overview={overview}
                activeSeconds={0}
                sessionActive={false}
                copy={runtime}
              />
              <MockSessionHistoryCard
                overview={overview}
                confirmClear={clearHistoryPrompt}
                clearing={clearingHistory}
                notice={historyNotice}
                onRequestClear={() => {
                  setHistoryNotice("");
                  setClearHistoryPrompt(true);
                }}
                onCancelClear={() => setClearHistoryPrompt(false)}
                onConfirmClear={() => void clearMockHistory()}
                copy={runtime}
              />
            </aside>
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="space-y-5">
              {question ? (
                <section className="overflow-hidden rounded-2xl bg-slate-950 text-white shadow-xl">
                  <div className="grid gap-6 p-6 md:grid-cols-[250px_1fr] md:p-8">
                    <InterviewerAvatar avatar={progress.config.avatar} state={avatarState} mouth={mouth} customImageUrl={customAvatarUrl} />
                    <div className="self-center">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-300">{question.is_follow_up ? runtime.adaptiveFollowup : question.competency}</p>
                      <h2 className="mt-3 text-2xl font-semibold leading-relaxed">{question.text}</h2>
                      <div className="mt-6 flex flex-wrap gap-2">
                        <button type="button" onClick={toggleInterviewerAudio} disabled={recording} className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
                          {interviewerMuted ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}{interviewerMuted ? runtime.unmuteInterviewer : runtime.muteInterviewer}
                        </button>
                        <button type="button" onClick={() => { spokenQuestion.current = ""; setInterviewerMuted(false); setProgress({ ...progress }); }} disabled={recording} className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
                          <RotateCcw className="h-4 w-4" />{runtime.repeatQuestion}
                        </button>
                      </div>
                      <div className="mt-5">
                        <VisualDeliverySessionPanel controller={visualDelivery} />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-800 bg-slate-900 p-5">
                    {recording ? (
                      <MicrophoneWaveform stream={stream.current} title={runtime.recordingAnswer} subtitle={runtime.stopSubmitHint} ariaLabel={runtime.candidateMicAria} />
                    ) : null}
                    <div className={`flex flex-col items-center justify-between gap-4 ${recording ? "mt-4" : ""} sm:flex-row`}>
                      <div>
                        <p className="text-sm font-semibold">{recording ? runtime.microphoneLive : runtime.readyAnswer}</p>
                        <p className="mt-1 text-xs text-slate-400">{runtime.answerControlHelp}</p>
                      </div>
                      {recording ? (
                        <button type="button" onClick={stopRecording} className="inline-flex items-center gap-2 rounded-full bg-red-500 px-6 py-3 font-semibold text-white hover:bg-red-600"><Square className="h-4 w-4" />{runtime.stopSubmit}</button>
                      ) : (
                        <button type="button" onClick={() => void beginRecording()} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-6 py-3 font-semibold text-white hover:bg-indigo-600 disabled:opacity-50"><Mic className="h-4 w-4" />{runtime.answerWithMic}</button>
                      )}
                    </div>
                  </div>
                </section>
              ) : null}

              <section className="grid gap-4 md:grid-cols-2" aria-label={runtime.assistanceAria}>
                {resultPanels.map((panel) => (
                  <article key={panel.title} className="flex min-h-44 flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-semibold text-slate-950">{panel.title}</h3>
                      <button
                        type="button"
                        onClick={() => setFocusedPanel(panel)}
                        disabled={!mockPanelText(panel.value)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                      >
                        <Maximize2 className="h-3.5 w-3.5" /> Focus
                      </button>
                    </div>
                    <div className="mt-3 text-sm leading-6 text-slate-700">
                    <MockPanelContent value={panel.value} emptyText={runtime.resultEmpty} />
                    </div>
                  </article>
                ))}
              </section>

              {progress.status === "completed" && !report ? (
                <section className="rounded-2xl border border-indigo-200 bg-white p-6 text-center shadow-sm">
                  <h2 className="text-xl font-bold text-slate-950">{runtime.interviewComplete}</h2>
                  <p className="mt-2 text-sm text-slate-600">{runtime.interviewCompleteBody}</p>
                  <button type="button" onClick={() => void buildReport()} disabled={busy} className="mt-4 rounded-full bg-indigo-600 px-6 py-3 font-semibold text-white disabled:opacity-50">{busy ? runtime.buildingReport : runtime.viewReport}</button>
                </section>
              ) : null}

              {progress.config.coaching_mode && progress.evaluations?.length ? (
                <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6"><h3 className="font-semibold">{runtime.coachingFeedback}</h3><p className="mt-2 text-sm text-slate-700">{runtimeText(runtime.latestScore, { score: averageRubric(progress.evaluations.at(-1)?.rubric).toFixed(2) })}</p><ul className="mt-2 list-disc pl-5 text-sm">{progress.evaluations.at(-1)?.improvements?.map((item: string) => <li key={item}>{item}</li>)}</ul></section>
              ) : null}
              {report ? <Report report={report} copy={runtime} /> : null}
              {report || endedEarly ? (
                <VisualDeliveryResults summary={visualDelivery.summary} />
              ) : null}
            </div>

            <aside className="space-y-4">
              <MockMonthlyUsageCard
                overview={overview}
                activeSeconds={elapsedSeconds}
                sessionActive={!endedEarly && !report}
                copy={runtime}
              />
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{runtime.interviewProgress}</p>
                <p className="mt-3 text-3xl font-bold text-slate-950">{Math.min(progress.currentIndex + 1, progress.totalQuestions)}<span className="text-base font-medium text-slate-400"> / {progress.totalQuestions}</span></p>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-indigo-600" style={{ width: `${Math.min(100, ((progress.currentIndex + 1) / Math.max(1, progress.totalQuestions)) * 100)}%` }} /></div>
                <p className="mt-3 text-sm text-slate-600">{runtimeText(runtime.answersSubmitted, { count: progress.completedAnswers })}</p>
              </section>
              <section className="rounded-2xl border border-indigo-100 bg-indigo-50 p-5 text-sm text-indigo-950">
                <p className="font-semibold">{runtime.sameControls}</p>
                <ul className="mt-2 space-y-2 text-xs leading-5 text-indigo-800"><li>{runtime.spacebarControl}</li><li>{runtime.answerControl}</li><li>{runtime.timeControl}</li></ul>
              </section>
              <section className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-700 shadow-sm">
                <p className="font-semibold text-slate-950">{runtime.activeSources}</p>
                <ul className="mt-2 space-y-2 text-xs leading-5"><li>{runtime.interviewerSource}</li><li>{runtime.candidateSource}</li></ul>
              </section>
              <MockSessionHistoryCard
                overview={overview}
                confirmClear={clearHistoryPrompt}
                clearing={clearingHistory}
                notice={historyNotice}
                onRequestClear={() => {
                  setHistoryNotice("");
                  setClearHistoryPrompt(true);
                }}
                onCancelClear={() => setClearHistoryPrompt(false)}
                onConfirmClear={() => void clearMockHistory()}
                copy={runtime}
              />
            </aside>
          </div>
        )}
        {busy && question ? <p className="rounded-lg bg-blue-50 p-3 text-sm text-blue-700">{runtime.processingSecurely}</p> : null}
        {connectionNotice ? <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900" role="status">{connectionNotice}</p> : null}
        {error ? <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
      </main>
    </div>
  );
}

type MockResultPanel = {
  title: string;
  value: unknown;
};

function mockPanelText(value: unknown): string {
  if (Array.isArray(value)) return value.map(mockPanelText).filter(Boolean).join(". ");
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>)
      .map(mockPanelText)
      .filter(Boolean)
      .join(". ");
  }
  return typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
}

function MockPanelContent({ value, emptyText }: { value: unknown; emptyText: string }) {
  if (Array.isArray(value) && value.length > 0) {
    return <ul className="list-disc space-y-1 pl-5">{value.map((item, index) => <li key={`${mockPanelText(item)}-${index}`}>{mockPanelText(item)}</li>)}</ul>;
  }
  const text = mockPanelText(value);
  return <p className={text ? "" : "text-slate-400"}>{text || emptyText}</p>;
}

function MockFocusedPanel({
  panel,
  language,
  copy,
  onClose,
}: {
  panel: MockResultPanel | null;
  language: string;
  copy: InterviewRuntimeCopy;
  onClose: () => void;
}) {
  const [speechStatus, setSpeechStatus] = useState<"idle" | "listening" | "unavailable">("idle");

  const stopListening = useCallback(() => {
    if (typeof window !== "undefined") window.speechSynthesis?.cancel();
    setSpeechStatus("idle");
  }, []);

  const startListening = useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis || !panel) {
      setSpeechStatus("unavailable");
      return;
    }
    const text = mockPanelText(panel.value);
    if (!text) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language || "en";
    utterance.onstart = () => setSpeechStatus("listening");
    utterance.onend = () => setSpeechStatus("idle");
    utterance.onerror = () => setSpeechStatus("unavailable");
    window.speechSynthesis.speak(utterance);
  }, [language, panel]);

  useEffect(() => stopListening, [panel, stopListening]);

  if (!panel) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true" aria-label={panel.title} onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-slate-950">{panel.title}</h2>
          <div className="flex items-center gap-2">
            {speechStatus === "listening" ? (
              <button type="button" onClick={stopListening} className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white"><Square className="h-4 w-4 fill-white" /> {copy.stop}</button>
            ) : (
              <button type="button" onClick={startListening} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white"><Volume2 className="h-4 w-4" /> {copy.listen}</button>
            )}
            <button type="button" onClick={() => { stopListening(); onClose(); }} className="rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label={copy.closeFocused}><X className="h-5 w-5" /></button>
          </div>
        </div>
        <p className="mb-4 mt-3 text-sm font-medium text-slate-500" aria-live="polite">
          {speechStatus === "listening" ? copy.listeningPanel : speechStatus === "unavailable" ? copy.ttsUnavailable : copy.readyListen}
        </p>
        <MockPanelContent value={panel.value} emptyText={copy.resultEmpty} />
      </div>
    </div>
  );
}

function MockMonthlyUsageCard({
  overview,
  activeSeconds,
  sessionActive,
  copy,
}: {
  overview: MockOverview;
  activeSeconds: number;
  sessionActive: boolean;
  copy: InterviewRuntimeCopy;
}) {
  const usage = overview.usage;
  const limitSeconds = Math.max(0, Number(usage?.limitSeconds) || 0);
  const usedSeconds = Math.max(0, Number(usage?.usedSeconds) || 0);
  const reservedSeconds = Math.max(0, Number(usage?.reservedSeconds) || 0);
  const availableSeconds = Math.max(0, Number(usage?.remainingSeconds) || 0);
  const sessionRemainingSeconds = Math.max(0, reservedSeconds - (sessionActive ? activeSeconds : 0));
  const percentUsed =
    limitSeconds > 0
      ? Math.min(100, ((usedSeconds + reservedSeconds) / limitSeconds) * 100)
      : 0;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Clock3 className="h-4 w-4 text-indigo-600" />
          <h2 className="font-semibold text-slate-950">
            {copy.monthlyMockTime}
          </h2>
        </div>
        {usage?.plan ? (
          <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold capitalize text-indigo-700">
            {usage.plan}
          </span>
        ) : null}
      </div>

      {overview.loading ? (
        <p className="mt-4 text-sm text-slate-500">{copy.loadingAllowance}</p>
      ) : !usage ? (
        <p className="mt-4 text-sm text-slate-500">
          {overview.unavailable
            ? copy.allowanceUnavailable
            : copy.allowanceRequiresPlan}
        </p>
      ) : (
        <>
          <div className="mt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{copy.timeRemaining}</p>
            <p className="mt-1 font-mono text-2xl font-bold text-indigo-700">{formatQuotaClock(availableSeconds)}</p>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <UsageMetric label={copy.used} seconds={usedSeconds} />
            <UsageMetric label={copy.reserved} seconds={reservedSeconds} />
            <UsageMetric label={copy.available} seconds={availableSeconds} />
          </div>
          <p className="mt-3 text-xs text-slate-500">
            {runtimeText(copy.minutesUsed, { used: (usedSeconds / 60).toFixed(1), limit: Math.round(limitSeconds / 60) })}
          </p>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-indigo-600 transition-[width]"
              style={{ width: `${percentUsed}%` }}
            />
          </div>
          <p className="mt-3 text-xs text-slate-500">
            {runtimeText(copy.resets, { date: new Date(usage.resetAt).toLocaleString() })}
          </p>
          {sessionActive ? (
            <div className="mt-3 rounded-xl p-3 text-xs font-medium text-emerald-700">
              <p className="font-mono text-lg font-bold">{formatQuotaClock(sessionRemainingSeconds)}</p>
              <p>{copy.activeBilling}</p>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function UsageMetric({ label, seconds }: { label: string; seconds: number }) {
  return (
    <div className="rounded-xl bg-slate-50 px-2 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 font-mono text-sm font-bold text-slate-900">{formatQuotaClock(seconds)}</p>
    </div>
  );
}

function MockSessionHistoryCard({
  overview,
  confirmClear,
  clearing,
  notice,
  onRequestClear,
  onCancelClear,
  onConfirmClear,
  copy,
}: {
  overview: MockOverview;
  confirmClear: boolean;
  clearing: boolean;
  notice: string;
  onRequestClear: () => void;
  onCancelClear: () => void;
  onConfirmClear: () => void;
  copy: InterviewRuntimeCopy;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold text-slate-950">{copy.sessionHistory}</h2>
        <button
          type="button"
          onClick={onRequestClear}
          disabled={overview.loading || clearing || overview.history.length === 0}
          className="rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {copy.clear}
        </button>
      </div>
      {confirmClear ? (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3" role="alert">
          <p className="text-xs leading-5 text-red-900">
            {copy.clearConfirm}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onCancelClear}
              disabled={clearing}
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
            >
              {copy.cancel}
            </button>
            <button
              type="button"
              onClick={onConfirmClear}
              disabled={clearing}
              className="rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              {clearing ? copy.clearing : copy.clearHistory}
            </button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <p className="mt-3 text-xs font-medium text-slate-600" role="status">
          {notice}
        </p>
      ) : null}
      {overview.loading ? (
        <p className="mt-4 text-sm text-slate-500">{copy.loadingSessions}</p>
      ) : overview.history.length === 0 ? (
        <div className="py-7 text-center text-sm text-slate-500">
          <Clock3 className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2">{copy.noSessions}</p>
        </div>
      ) : (
        <ul className="mt-4 max-h-[420px] space-y-2 overflow-y-auto pr-1">
          {overview.history.map((entry) => (
            <li
              key={entry.sessionId}
              className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-700">
                    {mockHistoryTitle(entry, copy)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {new Date(entry.endedAt ?? entry.startedAt).toLocaleString()}
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[11px] font-semibold capitalize text-slate-600">
                  {entry.status === "active" ? copy.inProgress : entry.status}
                </span>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {runtimeText(copy.historyUsed, { time: formatQuotaClock(Math.max(0, Number(entry.billableSeconds) || 0)) })}
              </p>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs leading-5 text-slate-500">
        {copy.historyPrivacy}
      </p>
    </section>
  );
}

function mockHistoryTitle(entry: MockHistoryEntry, copy: InterviewRuntimeCopy): string {
  const role = String(entry.settings?.role ?? "").trim();
  if (role) return `${role} · ${copy.mockInterview}`;
  const type = String(entry.settings?.interviewType ?? "").trim();
  if (!type) return copy.mockInterview;
  const label = type.replaceAll("_", " ").replaceAll("-", " ");
  return `${label.charAt(0).toUpperCase()}${label.slice(1)} · ${copy.mockInterview}`;
}

function formatQuotaClock(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
}

function averageRubric(rubric: Record<string, number> | undefined) {
  const values = Object.values(rubric ?? {});
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

function formatDuration(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function Readiness({ ready, label }: { ready: boolean; label: string }) {
  return (
    <span className={`rounded-lg px-3 py-2 ${ready ? "bg-emerald-50 text-emerald-700" : "bg-slate-100"}`}>
      {ready ? "✓" : "○"} {label}
    </span>
  );
}

function SetupSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="text-sm font-medium text-slate-700">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 bg-white p-3 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500">
        {children}
      </select>
    </label>
  );
}

function Report({ report, copy }: { report: Record<string, any>; copy: InterviewRuntimeCopy }) {
  return (
    <section className="space-y-5 rounded-2xl border bg-white p-8 shadow-sm">
      <div>
        <p className="text-sm font-semibold uppercase text-blue-600">{copy.finalReport}</p>
        <h2 className="mt-2 text-4xl font-bold">{Number(report.overall_score ?? 0).toFixed(2)} / 5</h2>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {Object.entries(report.rubric_averages ?? {}).map(([key, value]) => (
          <div key={key} className="rounded-lg bg-slate-50 p-4">
            <p className="text-sm capitalize text-slate-600">{key.replaceAll("_", " ")}</p>
            <p className="text-xl font-semibold">{Number(value).toFixed(2)} / 5</p>
          </div>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="font-semibold">{copy.strongest}</h3>
          <p className="mt-2 text-sm text-slate-600">{(report.strongest_answers ?? []).join(", ") || copy.none}</p>
        </div>
        <div>
          <h3 className="font-semibold">{copy.weakest}</h3>
          <p className="mt-2 text-sm text-slate-600">{(report.weakest_answers ?? []).join(", ") || copy.none}</p>
        </div>
      </div>
      <div>
        <h3 className="font-semibold">{copy.practiceAreas}</h3>
        <ul className="mt-2 list-disc pl-5">
          {(report.recommended_practice_areas ?? []).map((item: string) => <li key={item}>{item}</li>)}
        </ul>
      </div>
      <div>
        <h3 className="font-semibold">{copy.confidenceIndicators}</h3>
        <ul className="mt-2 list-disc pl-5">
          {(report.confidence_indicators ?? []).map((item: string) => <li key={item}>{item}</li>)}
        </ul>
      </div>
      <div>
        <h3 className="font-semibold">{copy.improvedAnswers}</h3>
        {Object.entries(report.improved_sample_answers ?? {}).map(([id, value]) => (
          <article key={id} className="mt-3 rounded-lg border p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">{id}</p>
            <p className="mt-2 whitespace-pre-wrap text-sm">{String(value)}</p>
          </article>
        ))}
      </div>
      <p className="rounded-lg bg-slate-100 p-4 text-sm text-slate-600">{report.disclaimer}</p>
    </section>
  );
}
